/**
 * ActivityLog — per-worker lane view of tool-call activity for the Deep Explorer side panel.
 * Events come from `deep_activity_event` WS frames accumulated in the store.
 *
 * The previous stream/lanes/details/filter toggles were removed to simplify the
 * surface; lanes are the only view, and expanding a lane reveals each event's
 * detail lines (UX feedback round 2026-05).
 */
import { useTranslation } from "react-i18next"

import type { DeepActivityEvent, OrchestratorReasoningBlock } from "@/stores/ai-explorer-store"

import { WorkerLanes } from "./WorkerLanes"

interface ActivityLogProps {
  events: DeepActivityEvent[]
  /** B7: live subagent token streams keyed by `dispatch_id`. Threaded
   *  through to WorkerLanes so each running `task` row can show its
   *  subagent's reasoning live, not just the buffered final report. */
  liveSubagentReports?: Record<string, string>
  /** B4: orchestrator reasoning blocks streamed live between tool calls.
   *  Threaded into WorkerLanes for the orchestrator lane only. */
  orchestratorReasoning?: OrchestratorReasoningBlock[]
  /** Round-4: per-subagent-dispatch reasoning blocks for interleaving in
   *  the matching subagent lane. */
  subagentReasoning?: Record<string, OrchestratorReasoningBlock[]>
  /** Round-5: when true, auto-expand the `deep_explorer` (orchestrator)
   *  lane on first render. Set during the pre-approval planning phase so
   *  the planner's reasoning prose is visible immediately without a
   *  click. Subagent lanes stay collapsed-by-default. */
  autoExpandOrchestrator?: boolean
}

export function ActivityLog({
  autoExpandOrchestrator,
  events,
  liveSubagentReports,
  orchestratorReasoning,
  subagentReasoning,
}: ActivityLogProps) {
  const { t } = useTranslation()

  // Persistence layer may inject a `{ event: "truncated", dropped: N }`
  // sentinel when an old turn's activity log overflowed the Cosmos ceiling.
  // Pull it out of the rendered stream; it surfaces as a standalone banner.
  const truncatedSentinel = events.find((e) => (e.event as string) === "truncated") as
    | (DeepActivityEvent & { dropped?: number })
    | undefined
  const realEvents = truncatedSentinel
    ? events.filter((e) => (e.event as string) !== "truncated")
    : events

  if (realEvents.length === 0 && !truncatedSentinel) {
    return (
      <p className="text-muted-foreground py-3 text-center text-xs">
        {t("deepExplorer.no_activity_yet")}
      </p>
    )
  }

  const truncatedRow = truncatedSentinel ? (
    <p
      key="activity-truncated-sentinel"
      className="text-muted-foreground/80 py-1 text-center text-[10px] italic"
      role="note"
    >
      {t("deepExplorer.activity_truncated", {
        count: Number(truncatedSentinel.dropped ?? 0),
        defaultValue: "{{count}} earlier events omitted",
      })}
    </p>
  ) : null

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {truncatedRow}
      <WorkerLanes
        autoExpandOrchestrator={autoExpandOrchestrator}
        events={realEvents}
        liveSubagentReports={liveSubagentReports}
        orchestratorReasoning={orchestratorReasoning}
        subagentReasoning={subagentReasoning}
      />
    </div>
  )
}
